import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Shield, Users, CheckCircle, Sparkles, Scale, Award, Heart, Lightbulb } from 'lucide-react';
import CTASection from '../components/CTASection';
import InquiryModal from '../components/InquiryModal';

const About = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContext, setModalContext] = useState('');

  const handleOpenModal = (context) => {
    setModalContext(context);
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen pt-16">
      <Helmet>
        <title>About Trademark Essential - Expert IP Protection & Brand Registration</title>
        <meta name="description" content="Learn about our mission to protect your brand. Experienced trademark attorneys dedicated to securing intellectual property rights for businesses." />
      </Helmet>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#2B0A12] via-[#3D1C24] to-[#2B0A12] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-[#F1F1F1] mb-6">About Trademark Essential</h1>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Your trusted partner in intellectual property protection, dedicated to safeguarding the innovations and brands that define your success.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
        {/* Decorative Background */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#FFB400] opacity-5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#D72638] opacity-5 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-[#FFB400] uppercase tracking-wide mb-2 animate-pulse">Our Mission</p>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2B0A12] mb-6">Safeguarding Innovation. Protecting Identity.</h2>
          </div>
          
          <div className="max-w-4xl mx-auto mb-16">
            <div className="bg-white rounded-2xl shadow-lg p-8 border-l-4 border-[#FFB400] hover:shadow-2xl transition-shadow duration-300">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                At Trademark Essential, we believe that every innovation deserves protection and every brand deserves recognition. Our mission is to provide accessible, expert legal services that empower entrepreneurs, businesses, and creators to protect their intellectual property with confidence.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                We combine legal expertise with modern technology to deliver a seamless registration experience. Our team of experienced IP attorneys is dedicated to ensuring your trademarks, copyrights, and patents receive the protection they deserve.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Users, title: 'Expert Legal Team', desc: 'Experienced IP attorneys committed to your success', color: 'from-blue-500 to-blue-600' },
              { icon: CheckCircle, title: 'Transparent Process', desc: 'Clear communication and pricing at every step', color: 'from-green-500 to-green-600' },
              { icon: Shield, title: 'Comprehensive Services', desc: 'Complete IP protection from registration to enforcement', color: 'from-purple-500 to-purple-600' },
              { icon: Award, title: 'Client Success', desc: 'Your satisfaction and protection are our top priorities', color: 'from-orange-500 to-orange-600' }
            ].map((item, index) => (
              <Card key={index} className="group p-6 text-center hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 hover:border-[#FFB400] hover:-translate-y-2 bg-white">
                <div className={`w-20 h-20 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-lg`}>
                  <item.icon className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-lg font-bold text-[#2B0A12] mb-2 group-hover:text-[#D72638] transition-colors">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-[#FFB400] uppercase tracking-wide mb-2">Our Values</p>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2B0A12] mb-4">What We Stand For</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our core values guide every decision we make and every service we provide.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { 
                icon: Scale, 
                title: 'Integrity', 
                desc: 'We uphold the highest standards of legal ethics and professional conduct in every case we handle.',
                gradient: 'from-[#2B0A12] via-[#3D1C24] to-[#2B0A12]'
              },
              { 
                icon: Award, 
                title: 'Excellence', 
                desc: 'Our commitment to excellence drives us to deliver superior legal services and successful outcomes.',
                gradient: 'from-[#D72638] via-[#e74c3c] to-[#D72638]'
              },
              { 
                icon: Heart, 
                title: 'Client-Focused', 
                desc: 'Your success is our priority. We provide personalized attention and tailored legal strategies.',
                gradient: 'from-[#FFB400] via-[#f39c12] to-[#FFB400]'
              },
              { 
                icon: Lightbulb, 
                title: 'Innovation', 
                desc: 'We leverage modern technology and innovative approaches to streamline the IP protection process.',
                gradient: 'from-purple-600 via-purple-500 to-purple-600'
              }
            ].map((value, index) => (
              <Card key={index} className="group relative overflow-hidden p-8 hover:shadow-2xl transition-all duration-500 border-2 border-transparent hover:border-[#FFB400]">
                {/* Animated Background on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${value.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>
                
                <div className="relative z-10">
                  <div className={`w-20 h-20 bg-gradient-to-br ${value.gradient} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-12 transition-all duration-500 shadow-lg`}>
                    <value.icon className="h-10 w-10 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#2B0A12] mb-4 group-hover:text-[#D72638] transition-colors duration-300">{value.title}</h3>
                  <div className="h-1 w-12 bg-gradient-to-r from-[#FFB400] to-[#D72638] rounded-full mb-4 group-hover:w-full transition-all duration-500"></div>
                  <p className="text-gray-600 leading-relaxed">{value.desc}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#FFB400] opacity-10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-72 h-72 bg-[#D72638] opacity-10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-[#FFB400] uppercase tracking-wide mb-2">Our Team</p>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2B0A12] mb-4">Expert Legal Professionals</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our team of specialized attorneys brings deep expertise in intellectual property law.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { 
                role: 'Senior IP Attorney', 
                title: 'Lead Trademark Specialist',
                desc: 'Extensive experience in trademark law with hundreds of successful registrations',
                bgGradient: 'from-blue-500 to-cyan-500'
              },
              { 
                role: 'Patent Attorney', 
                title: 'Innovation Protection Expert',
                desc: 'Specialized in securing patents for cutting-edge technologies and inventions',
                bgGradient: 'from-purple-500 to-pink-500'
              },
              { 
                role: 'Copyright Specialist', 
                title: 'Creative Rights Attorney',
                desc: 'Dedicated to protecting creative works and digital content rights',
                bgGradient: 'from-orange-500 to-red-500'
              },
              { 
                role: 'Legal Strategist', 
                title: 'IP Portfolio Manager',
                desc: 'Expert in building comprehensive intellectual property portfolios',
                bgGradient: 'from-green-500 to-teal-500'
              }
            ].map((member, index) => (
              <Card key={index} className="group relative p-6 text-center hover:shadow-2xl transition-all duration-500 border-2 border-gray-100 hover:border-[#FFB400] hover:-translate-y-3 bg-white overflow-hidden">
                {/* Hover Background Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#FFB400]/5 to-[#D72638]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="relative z-10">
                  {/* Avatar with Animated Border */}
                  <div className="relative mx-auto mb-6 w-36 h-36">
                    <div className={`absolute inset-0 bg-gradient-to-br ${member.bgGradient} rounded-full blur-md opacity-50 group-hover:blur-lg group-hover:opacity-75 transition-all duration-500`}></div>
                    <div className={`relative w-36 h-36 bg-gradient-to-br ${member.bgGradient} rounded-full flex items-center justify-center shadow-xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                      <Users className="h-20 w-20 text-white" />
                    </div>
                  </div>
                  
                  <h3 className="text-lg font-bold text-[#2B0A12] mb-2 group-hover:text-[#D72638] transition-colors duration-300">{member.role}</h3>
                  <div className="h-1 w-16 bg-gradient-to-r from-[#FFB400] to-[#D72638] rounded-full mx-auto mb-3 group-hover:w-full transition-all duration-500"></div>
                  <p className="text-[#D72638] font-semibold mb-3">{member.title}</p>
                  <p className="text-sm text-gray-600 leading-relaxed">{member.desc}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 overflow-hidden">
        {/* Animated Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#2B0A12] via-[#3D1C24] to-[#2B0A12]">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#FFB400] opacity-20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#D72638] opacity-20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.5s' }}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-purple-500 opacity-10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '3s' }}></div>
        </div>
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-12 border border-white/10 shadow-2xl">
            <div className="text-center">
              {/* Icon */}
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-[#FFB400] to-[#D72638] rounded-2xl mb-6 shadow-lg">
                <Shield className="h-10 w-10 text-white" />
              </div>
              
              <h2 className="text-4xl md:text-5xl font-bold text-[#F1F1F1] mb-6">Ready to Protect Your Brand?</h2>
              <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto leading-relaxed">
                Let our expert team help you secure your intellectual property rights.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  size="lg"
                  onClick={() => handleOpenModal('Start Trademark Registration - About CTA')}
                  className="group relative bg-[#FFB400] hover:bg-[#e5a200] text-[#2B0A12] font-semibold text-lg px-10 py-7 rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 overflow-hidden"
                >
                  <span className="relative z-10">Start Trademark Registration</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-[#FFB400] to-[#D72638] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => handleOpenModal('Discuss with Attorney - About CTA')}
                  className="group relative border-2 border-[#D72638] text-[#F1F1F1] hover:bg-[#D72638] hover:text-white font-semibold text-lg px-10 py-7 rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-[#D72638]/50"
                >
                  <span className="relative z-10">Discuss with Attorney</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Inquiry Modal */}
      <InquiryModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        buttonContext={modalContext}
      />
    </div>
  );
};

export default About;